import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import styles from './Navbar.module.css';

function DiceIcon() {
  return (
    <svg className={styles.diceIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" fill="rgba(0,212,255,0.12)" stroke="#00d4ff" strokeWidth="1.5" />
      <circle cx="7.5" cy="7.5" r="1.8" fill="#00d4ff" />
      <circle cx="16.5" cy="7.5" r="1.8" fill="#00d4ff" />
      <circle cx="12" cy="12" r="1.8" fill="#f4c542" />
      <circle cx="7.5" cy="16.5" r="1.8" fill="#ff6b00" />
      <circle cx="16.5" cy="16.5" r="1.8" fill="#ff6b00" />
    </svg>
  );
}

function CoinIcon() {
  return (
    <svg className={styles.coinIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="rgba(244,197,66,0.15)" stroke="#f4c542" strokeWidth="1.5" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="700" fill="#f4c542">₹</text>
    </svg>
  );
}

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, user, canCreateGames } = useAuth();
  const [playerName, setPlayerName] = useState('');
  const walletCredits = Number(user?.credits ?? 0);
  const isLobby = location.pathname === '/' || location.pathname === '/login';

  useEffect(() => {
    const syncName = () => setPlayerName(localStorage.getItem('playerName') || '');
    syncName();
    window.addEventListener('storage', syncName);
    window.addEventListener('ludo-player-name-changed', syncName);
    return () => {
      window.removeEventListener('storage', syncName);
      window.removeEventListener('ludo-player-name-changed', syncName);
    };
  }, []);

  const navItems = [
    { label: 'Lobby', path: '/' },
    { label: 'Modes', path: '/game-modes' },
    ...(isAdmin ? [{ label: 'Admin', path: '/admincp/admin' }] : []),
  ];

  const roleText = user?.role === 'admin' ? 'Admin' : user?.role === 'api_manager' ? 'API' : 'Player';
  const roleClass = user?.role === 'admin'
    ? styles.roleAdmin
    : user?.role === 'api_manager'
      ? styles.roleApi
      : styles.rolePlayer;

  return (
    <header className={styles.header}>
      <button type="button" className={styles.brand} onClick={() => navigate('/')} aria-label="Open LudoKing lobby">
        <span className={styles.brandIcon}><DiceIcon /></span>
        <span className={styles.brandCopy}>
          <span className={styles.brandTitle}>LudoKing</span>
          <span className={styles.brandSub}>Gaming Platform</span>
        </span>
      </button>

      <div className={styles.spacer} />

      <nav className={styles.desktopNav} aria-label="Desktop navigation">
        {navItems.map(item => {
          const active = item.path === '/'
            ? location.pathname === '/' || location.pathname === '/login'
            : location.pathname.startsWith(item.path);
          return (
            <button
              type="button"
              key={item.path}
              className={`${styles.navButton} ${active ? styles.navActive : ''}`}
              onClick={() => navigate(item.path)}
            >
              {item.label}
            </button>
          );
        })}
      </nav>

      <span className={styles.divider} aria-hidden="true" />

      <div className={styles.wallet} title="Wallet credits" aria-label={`${walletCredits.toLocaleString('en-US')} wallet credits`}>
        <CoinIcon />
        <span className={styles.walletAmount}>{walletCredits.toLocaleString('en-US')}</span>
      </div>

      {playerName ? (
        <div className={styles.playerBadge} title={playerName}>
          <span className={styles.onlineDot} aria-label="Online" />
          <span className={styles.playerAvatar}>{playerName.charAt(0).toUpperCase()}</span>
          <span className={styles.playerName}>{playerName}</span>
        </div>
      ) : (
        <div className={styles.guestBadge}>
          <span className={styles.guestDot} />
          <span>Guest</span>
        </div>
      )}

      {user ? (
        <div className={styles.authGroup}>
          <span className={`${styles.roleBadge} ${roleClass}`}>{roleText}</span>
          {canCreateGames && isLobby && (
            <button
              type="button"
              className={styles.hostButton}
              onClick={() => window.dispatchEvent(new CustomEvent('open-host-modal'))}
            >
              <span className={styles.hostPlus}>+</span><span className={styles.hostText}> Host</span>
            </button>
          )}
        </div>
      ) : (
        <a className={styles.signIn} href="/auth/login" aria-label="Sign in">Sign In</a>
      )}
    </header>
  );
}
